import React from "react";
import { useParams, Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/ventrix/PublicLayout";
import { useApiQuery } from "@/hooks/useApi";
import { ArrowRight, Check } from "lucide-react";

export default function Service() {
  const { slug } = useParams();
  const { data, isPending, isError } = useApiQuery(["service", slug], `/v1/public/services/${slug}`);
  const s = data?.service;

  if (isPending) {
    return (
      <div className="min-h-screen">
        <PublicHeader />
        <div className="vtx-container py-24"><div className="vtx-skeleton h-64 w-full" /></div>
        <PublicFooter />
      </div>
    );
  }

  if (isError || !s) {
    return (
      <div className="min-h-screen">
        <PublicHeader />
        <div className="vtx-container py-28 text-center">
          <div className="vtx-eyebrow">/ Services</div>
          <h1 className="mt-3 text-3xl font-black text-[#0E2F76] tracking-tighter">Service not found</h1>
          <p className="mt-2 text-[#475569]">That service isn't available.</p>
          <Link to="/" className="vtx-btn-primary inline-flex items-center gap-2 mt-6">Back home <ArrowRight size={16} /></Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PublicHeader />
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img src={s.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(14,47,118,0.94) 0%, rgba(14,47,118,0.7) 100%)" }} />
        </div>
        <div className="relative vtx-container py-20 md:py-28 text-white">
          <div className="vtx-eyebrow text-[#A9C0E0]">/ Services · {s.title}</div>
          <h1 className="mt-4 text-4xl md:text-6xl font-black tracking-tighter">{s.title}</h1>
          <p className="mt-4 text-[#DDE7F5] text-lg max-w-2xl">{s.tagline}</p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/quote" className="vtx-btn-danger inline-flex items-center gap-2" data-testid="service-quote-cta">Get an instant quote <ArrowRight size={16}/></Link>
            <Link to="/track" className="vtx-btn-ghost text-white border-white/70 hover:bg-white hover:text-[#0E2F76]" data-testid="service-track-cta">Track a shipment</Link>
          </div>
        </div>
      </section>

      {(s.stats || []).length > 0 && (
        <section className="vtx-container py-16 grid md:grid-cols-3 gap-6">
          {(s.stats || []).map(([v, l]) => (
            <div key={l} className="vtx-card p-6">
              <div className="text-3xl md:text-4xl font-black text-[#0E2F76]">{v}</div>
              <div className="mt-1 text-[10px] tracking-[0.22em] uppercase font-bold text-[#475569]">{l}</div>
            </div>
          ))}
        </section>
      )}

      <section className="vtx-container pb-24">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="vtx-eyebrow">What's included</div>
            <ul className="mt-6 space-y-4">
              {(s.bullets || []).map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 grid place-items-center bg-[#0E2F76] shrink-0" style={{ borderRadius: 2 }}>
                    <Check size={12} color="white" strokeWidth={3} />
                  </span>
                  <span className="text-[#0f172a] leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="vtx-card p-8 bg-[#0E2F76] text-white relative overflow-hidden">
            <div className="absolute inset-0 vtx-hero-grid opacity-30" />
            <div className="relative">
              <div className="vtx-eyebrow text-[#A9C0E0]">Ready to move?</div>
              <h3 className="mt-3 text-2xl font-black">Book your {s.title.toLowerCase()} shipment.</h3>
              <p className="mt-3 text-[#A9C0E0] text-sm">Open a corporate account to unlock negotiated rates, saved shippers/consignees, and API access.</p>
              <div className="mt-6 flex gap-3">
                <Link to="/register" className="vtx-btn-danger">Open account</Link>
                <Link to="/quote" className="vtx-btn-ghost text-white border-white/70 hover:bg-white hover:text-[#0E2F76]">Get quote</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
